import { Text } from '@/components/ui/text';
import { SettingsToggleItem } from '@/screens/settings/components/settings-toggle-item';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { View } from 'react-native';
import { useSetting } from '@/features/settings/hooks';
import type { ControlEntry } from '../registry';
import { SettingsSegmentedControl } from './settings-segmented-control';

interface SettingControlRowProps {
  entry: ControlEntry;
  isLast?: boolean;
  gatedDisabled?: boolean;
}

/**
 * Renders a schema-bound setting generically. A `toggle` reuses the existing
 * `SettingsToggleItem` (Switch); a `select` renders a segmented control. Both are
 * driven by `useSetting(settingKey)`, so the row stays value-schema agnostic.
 */
export function SettingControlRow({
  entry,
  isLast = false,
  gatedDisabled = false,
}: SettingControlRowProps) {
  const { value, setValue } = useSetting(entry.settingKey);

  if (entry.control === 'toggle') {
    return (
      <SettingsToggleItem
        isLast={isLast}
        item={{
          testID: `setting-${entry.settingKey}`,
          icon: entry.icon,
          title: entry.label,
          value: Boolean(value),
          onToggle: (next) => setValue(next),
          disabled: gatedDisabled,
        }}
      />
    );
  }

  const iconColor = gatedDisabled ? 'text-muted-foreground' : 'text-foreground';
  const labelColor = gatedDisabled ? 'text-muted-foreground' : 'text-foreground';

  return (
    <View
      testID={`setting-${entry.settingKey}`}
      className={`py-4 ${!isLast ? 'border-b border-border' : ''}`}
      style={{ opacity: gatedDisabled ? 0.4 : 1 }}
    >
      <View className="flex-row items-center">
        <View className="w-8 items-center mr-3">
          <Ionicons name={entry.icon as never} size={22} className={iconColor} />
        </View>
        <Text className={`flex-1 text-base ${labelColor}`}>{entry.label}</Text>
      </View>
      <SettingsSegmentedControl
        name={entry.settingKey}
        value={String(value)}
        options={entry.options ?? []}
        onChange={(next) => setValue(next as never)}
        disabled={gatedDisabled}
        groupLabel={entry.label}
      />
    </View>
  );
}
